/**
 * Timestamp helpers — shared between frontend and backend.
 *
 * `shared/` cannot import either Firebase SDK (client or admin), so these
 * helpers duck-type Timestamp-like values rather than referencing the SDK's
 * Timestamp class. Both SDKs expose a `toMillis()` method and the
 * `{seconds, nanoseconds}` shape, which is what we check here.
 */

interface TimestampLike {
  toMillis(): number
}

interface TimestampShape {
  seconds: number
  nanoseconds: number
}

function isTimestampLike(value: unknown): value is TimestampLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    'toMillis' in value &&
    typeof (value as { toMillis: unknown }).toMillis === 'function'
  )
}

function isTimestampShape(value: unknown): value is TimestampShape {
  return (
    typeof value === 'object' &&
    value !== null &&
    'seconds' in value &&
    'nanoseconds' in value &&
    typeof (value as TimestampShape).seconds === 'number' &&
    typeof (value as TimestampShape).nanoseconds === 'number'
  )
}

/**
 * Normalize a timestamp-ish value to Unix milliseconds.
 *
 * Accepts the set of shapes we see across the codebase during the Firestore
 * Timestamp migration window: SDK Timestamp objects, the raw
 * `{seconds, nanoseconds}` JSON shape Firestore emits over the wire, JS Date,
 * Unix ms numbers, and ISO strings. Returns 0 for null/undefined so callers
 * that compare "ms since last X" still work on absent fields.
 */
export function toMillis(value: unknown): number {
  if (value === null || value === undefined) return 0
  if (typeof value === 'number') return value
  if (value instanceof Date) return value.getTime()
  if (isTimestampLike(value)) return value.toMillis()
  if (isTimestampShape(value)) return value.seconds * 1000 + Math.floor(value.nanoseconds / 1e6)
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? 0 : parsed
  }
  return 0
}

/**
 * Serialize a timestamp-ish value to an ISO-8601 string for the API wire.
 *
 * Accepts the same shapes as `toMillis`. Returns `undefined` for null,
 * undefined, and unparseable values — so the field is simply omitted from
 * the JSON response rather than serialized as `null` or the broken string
 * `'Invalid Date'`. Callers that need a guaranteed string should provide
 * their own fallback.
 *
 * Detection is by input *shape*, not by the output of `toMillis` — a legit
 * epoch Timestamp (ms = 0) must still serialize to `1970-01-01T00:00:00.000Z`
 * rather than being dropped as "unparseable".
 */
/**
 * Serialise a Date only when it can be serialised at all.
 *
 * `Number.isFinite` is not enough: 1e20 is finite, `new Date(1e20)` is an Invalid
 * Date, and `.toISOString()` on it throws a RangeError. The same holds for a
 * Timestamp-like object whose `toMillis()` returns NaN or something absurd. This
 * function is documented as returning undefined for unparseable values, so every
 * path has to actually check the Date rather than the input that produced it.
 *
 * It throwing is not a cosmetic bug: callers serialise whole documents with it,
 * and a throw there escapes before any field-level recovery can run.
 */
const isoOrUndefined = (date: Date): string | undefined =>
  Number.isNaN(date.getTime()) ? undefined : date.toISOString()

export function toISOString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'number') {
    return Number.isFinite(value) ? isoOrUndefined(new Date(value)) : undefined
  }
  if (value instanceof Date) {
    return isoOrUndefined(value)
  }
  if (isTimestampLike(value)) {
    // toMillis() is somebody else's implementation; it can return anything.
    try {
      return isoOrUndefined(new Date(value.toMillis()))
    } catch {
      return undefined
    }
  }
  if (isTimestampShape(value)) {
    return isoOrUndefined(new Date(value.seconds * 1000 + Math.floor(value.nanoseconds / 1e6)))
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? undefined : isoOrUndefined(new Date(parsed))
  }
  return undefined
}
